/**
 * DEPARTMENT MANAGER - BUDGET MANAGEMENT
 * 
 * View and manage budget lines for their department
 * Can add lines, edit allocations, monitor consumption
 * Cannot create new budgets (Finance Admin only)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DollarSign, Plus, Edit, TrendingUp, AlertCircle, ChevronDown, ChevronRight, FileText, CheckCircle, Clock, RefreshCw, Search, ArrowUpDown, ArrowUp, ArrowDown, Inbox, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { budgetsAPI } from '../api/client';

const DepartmentBudgetManagement = () => {
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetLines, setBudgetLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLines, setExpandedLines] = useState({});
  const [lineTransactions, setLineTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('AccountCode');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadDepartmentBudgets();
  }, []);

  useEffect(() => {
    if (selectedBudget) {
      loadBudgetLines(selectedBudget.BudgetID);
    }
  }, [selectedBudget]);

  const loadDepartmentBudgets = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await budgetsAPI.getMyDepartmentBudgets();

      setBudgets(response.data.budgets || []);
      
      // Auto-select first budget if available
      if (response.data.budgets && response.data.budgets.length > 0) {
        setSelectedBudget(response.data.budgets[0]);
      }
    } catch (err) {
      console.error('Failed to load budgets:', err);
      setError(err.response?.data?.message || 'Failed to load department budgets');
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetLines = async (budgetId) => {
    try {
      // Use /manage endpoint which validates department manager access
      const response = await budgetsAPI.getForManagement(budgetId);

      setBudgetLines(response.data.lines || []);
    } catch (err) {
      console.error('Failed to load budget lines:', err);
      setError(err.response?.data?.message || 'Failed to load budget details');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const calculateUtilization = (spent, committed, budgeted) => {
    if (!budgeted || budgeted === 0) return 0;
    return ((parseFloat(spent || 0) + parseFloat(committed || 0)) / parseFloat(budgeted)) * 100;
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'text-red-600 bg-red-100';
    if (percentage >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const toggleLineExpand = async (lineId) => {
    const isExpanded = expandedLines[lineId];
    setExpandedLines(prev => ({ ...prev, [lineId]: !isExpanded }));

    // Load transactions if not already loaded
    if (!isExpanded && !lineTransactions[lineId]) {
      setLoadingTransactions(prev => ({ ...prev, [lineId]: true }));
      try {
        const response = await budgetsAPI.getLineTransactions(selectedBudget.BudgetID, lineId);
        setLineTransactions(prev => ({ ...prev, [lineId]: response.data.transactions || [] }));
      } catch (err) {
        console.error('Failed to load transactions:', err);
      } finally {
        setLoadingTransactions(prev => ({ ...prev, [lineId]: false }));
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'Pending':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3" /> Pending</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  }, [sortField]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const filteredLines = useMemo(() => {
    let result = [...budgetLines];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(line =>
        line.AccountCode?.toLowerCase().includes(term) ||
        line.AccountName?.toLowerCase().includes(term) ||
        line.DepartmentName?.toLowerCase().includes(term) ||
        line.ProjectCode?.toLowerCase().includes(term)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (['BudgetedAmount', 'SpentAmount', 'CommittedAmount', 'RemainingAmount'].includes(sortField)) {
        aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [budgetLines, searchTerm, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading budgets...</span>
        </div>
      </div>
    );
  }

  if (budgets.length === 0) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            No Budgets Assigned
          </h3>
          <p className="text-yellow-800 text-sm">
            You don't have any departments assigned to you, or your departments don't have budgets set up yet.
          </p>
          <p className="text-xs text-yellow-700 mt-2">
            Contact your Finance Admin to set up budgets for your departments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600" />
            Department Budget Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage budget allocations for your departments
          </p>
        </div>
        <button
          onClick={loadDepartmentBudgets}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Budget Selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
          Select Budget
        </label>
        <select
          value={selectedBudget?.BudgetID || ''}
          onChange={(e) => {
            const budget = budgets.find(b => b.BudgetID === e.target.value);
            setSelectedBudget(budget);
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {budgets.map((budget) => (
            <option key={budget.BudgetID} value={budget.BudgetID}>
              {budget.DepartmentName} - {budget.YearLabel || budget.FinancialYear} ({budget.BudgetName})
            </option>
          ))}
        </select>
      </div>

      {selectedBudget && (
        <>
          {/* Budget Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Budget</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedBudget.TotalBudget)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Spent</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedBudget.SpentAmount)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Committed</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedBudget.CommittedAmount)}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Remaining</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedBudget.RemainingAmount)}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* Budget Lines */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Budget Lines</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search lines..."
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
                  />
                </div>
                <Link
                  to={`/budget-lines?budgetId=${selectedBudget.BudgetID}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Line
                </Link>
              </div>
            </div>

            {filteredLines.length === 0 ? (
              <div className="p-8 text-center">
                <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">
                  {searchTerm ? 'No budget lines match your search' : 'No budget lines defined yet'}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm ? 'Try adjusting your search' : 'Click "Add Line" to create your first allocation'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                        
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('AccountCode')}>
                        <span className="inline-flex items-center">GL Account <SortIcon field="AccountCode" /></span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}>
                        <span className="inline-flex items-center">Department <SortIcon field="DepartmentName" /></span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BudgetedAmount')}>
                        <span className="inline-flex items-center justify-end">Budgeted <SortIcon field="BudgetedAmount" /></span>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('SpentAmount')}>
                        <span className="inline-flex items-center justify-end">Spent <SortIcon field="SpentAmount" /></span>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('CommittedAmount')}>
                        <span className="inline-flex items-center justify-end">Committed <SortIcon field="CommittedAmount" /></span>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('RemainingAmount')}>
                        <span className="inline-flex items-center justify-end">Remaining <SortIcon field="RemainingAmount" /></span>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilization
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLines.map((line) => {
                      const utilization = calculateUtilization(
                        line.SpentAmount,
                        line.CommittedAmount,
                        line.BudgetedAmount
                      );
                      const isExpanded = expandedLines[line.BudgetLineID];
                      const transactions = lineTransactions[line.BudgetLineID] || [];
                      const isLoadingTx = loadingTransactions[line.BudgetLineID];
                      const hasSpent = (line.SpentAmount || 0) > 0 || (line.CommittedAmount || 0) > 0;

                      return (
                        <React.Fragment key={line.BudgetLineID}>
                          <tr 
                            className={`transition-colors ${hasSpent ? 'cursor-pointer' : ''} hover:bg-gray-50`}
                            onClick={() => hasSpent && toggleLineExpand(line.BudgetLineID)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap w-8">
                              {hasSpent && (
                                isExpanded 
                                  ? <ChevronDown className="w-4 h-4 text-gray-500" />
                                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {line.AccountCode}
                              </div>
                              <div className="text-xs text-gray-500">{line.AccountName}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {line.DepartmentName || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {line.ProjectCode || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                              {formatCurrency(line.BudgetedAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-red-600">
                              {formatCurrency(line.SpentAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-yellow-600">
                              {formatCurrency(line.CommittedAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-green-600">
                              {formatCurrency(line.RemainingAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUtilizationColor(utilization)}`}>
                                {utilization.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="9" className="px-0 py-0">
                                <div className="bg-gray-50 border-t border-b border-gray-200 px-8 py-3">
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    Invoices charged to this budget line
                                  </h4>
                                  {isLoadingTx ? (
                                    <div className="text-sm text-gray-500 py-2">Loading transactions...</div>
                                  ) : transactions.length === 0 ? (
                                    <div className="text-sm text-gray-500 py-2">No invoices found for this budget line.</div>
                                  ) : (
                                    <table className="min-w-full text-sm">
                                      <thead>
                                        <tr className="text-xs text-gray-500 uppercase">
                                          <th className="text-left py-1 pr-4">Invoice</th>
                                          <th className="text-left py-1 pr-4">Vendor</th>
                                          <th className="text-left py-1 pr-4">Date</th>
                                          <th className="text-right py-1 pr-4">Amount</th>
                                          <th className="text-left py-1 pr-4">Status</th>
                                          <th className="text-left py-1">Approved By</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {transactions.map((tx, idx) => (
                                          <tr key={idx} className="border-t border-gray-200">
                                            <td className="py-2 pr-4 font-medium text-blue-700">
                                              {tx.InvoiceNumber}
                                            </td>
                                            <td className="py-2 pr-4 text-gray-700">
                                              {tx.VendorName || '-'}
                                            </td>
                                            <td className="py-2 pr-4 text-gray-600">
                                              {tx.InvoiceDate ? new Date(tx.InvoiceDate).toLocaleDateString('en-GB') : '-'}
                                            </td>
                                            <td className="py-2 pr-4 text-right font-medium text-gray-900">
                                              {formatCurrency(tx.LineAmount)}
                                            </td>
                                            <td className="py-2 pr-4">
                                              {getStatusBadge(tx.Status)}
                                            </td>
                                            <td className="py-2 text-gray-600">
                                              {tx.ApprovedBy || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DepartmentBudgetManagement;
